import { NaregLicenceDto, NaregPersonDetailedDto } from '@shared/service-proxies/service-proxies';
import { IHealthRegisterFullPersonInfo } from '../health-register.service';
import { AppConsts } from '@shared/AppConsts';
import { DateTime } from '@node_modules/@types/luxon';
import { Observable, of } from 'rxjs';
import { HealthRegisterDiploma } from './health-register-diploma';
import { HealthRegisterPostgraduateTitle } from './health-register-postgraduate-title';
import { HealthRegisterPostgraduateTitlePrivateLaw } from './health-register-postgraduate-title-private-law';
import { HealthRegisterBaseService } from '../health-register-base.service';
import { DatePipe } from '@node_modules/@angular/common';
import { Injector } from '@node_modules/@angular/core';

export class NaregFullPersonInfo extends HealthRegisterBaseService implements IHealthRegisterFullPersonInfo  {

    cantonId: string;

    constructor(
        private latestNaregPerson: NaregPersonDetailedDto,
        protected datePipe: DatePipe,
        injector: Injector,
    ) {
        super(datePipe, injector);
    }

    get healthRegisterLabel(): string {
        return 'Nareg';
    }

    get SearchSuccessfulMessage(): string {
        return this.l('SearchSuccessfulNaregMessage');
    }

    get SearchFailedMessageKey(): string {
        return 'SearchFailedNaregMessage';
    }

    personWasFound(): boolean {
        return !!this.latestNaregPerson;
    }

    get postgraduateTitles(): string[] {
       return null;
    }

    get fullName(): string {
        let person = this.latestNaregPerson;
        let name = person?.firstName;

        if (person?.lastName) {
            if (name) {
                name = name + ' ' + person.lastName;
            } else {
                name = person.lastName;
            }
        }

        return name;
    }

    get firstName(): string {
        return this.latestNaregPerson?.firstName;
    }

    get lastName(): string {
        return this.latestNaregPerson?.lastName;
    }

    get birthName(): string {
        return this.latestNaregPerson?.originalName;
    }

    get placeOfOrigin(): string {
        return this.latestNaregPerson?.placeOfOrigin;
    }

    get gender(): string {
        return this.latestNaregPerson?.sexId.sironaId;
    }

    get nationality1(): string {
        return this.latestNaregPerson?.country1.sironaId;
    }

    get nationality2(): string {
        return this.latestNaregPerson?.country2.sironaId;
    }

    get birthYear(): string {
        return this.latestNaregPerson?.birthYear;
    }

    get birthDate(): string {
        return this.resolveDate(this.latestNaregPerson?.dateOfBirth);
    }

    get ahv(): string {
        return this.latestNaregPerson?.ahv;
    }

    get gln(): string {
        return this.latestNaregPerson?.gln;
    }

    get languageSkills(): string[] {
        return [];
    }

    get languageSkillsAsCommaSeparatedList(): string {
        return null;
    }

    get queryTime(): DateTime {
        return this.latestNaregPerson?.queryTime;
    }

    get diplomas(): string[] {
        const list: string[] = [];
        let diplome = this.latestNaregPerson?.naregDiploms;
        if (diplome) {
            for (let i = 0; i < diplome.length; i++) {
                if (diplome[i].profession.sironaId != null) {
                    list.push(
                        `${this.localization.localize(diplome[i].profession.sironaId, this.lSource)}, ${this.resolveDate(diplome[i].dateOfIssue)}` +
                        `${diplome[i].country ? ', ' + this.localization.localize(diplome[i].country.sironaId, this.lSource) : ''}` +
                        `${diplome[i].diplomaType ? ', ' + this.localization.localize(diplome[i].diplomaType.sironaId, this.lSource) : ''}`
                    );
                }
            }
        }
        return list;
    }

    get hasSensitiveData(): boolean {
        const noSensitiveDataCode = 10002;
        const sensitiveData = this.latestNaregPerson?.naregSensitiveData?.filter(x => x.isDeleted === false && x.code.healthRegisterId !== noSensitiveDataCode);
        return sensitiveData?.length > 0;
    }

    get hasSensitiveDataCantonalLaw(): boolean {
        return false;
    }

    get cantonalLicenceRestrictions(): string[] {
        return this.latestNaregPerson?.cantonalLicenceRestrictions;
    }


    // getLanguage(naregId: number): Promise<string> {
    //     return new Promise((resolve) => {
    //         this.getPerson(naregId).subscribe((output: NaregPersonDetailedDto) => {
    //             const languageOfCorrespondence = output.correspondenceLanguage.sironaId;
    //             let language;
    //             if (languageOfCorrespondence == AppConsts.codes.language.german ||
    //                  languageOfCorrespondence == AppConsts.codes.language.french) {
    //                 language = languageOfCorrespondence;
    //             }
    //             resolve(language);
    //         });
    //     });
    // }

    getLicences(): NaregLicenceDto[] {
        return this.latestNaregPerson?.naregLicences;
    }

    get normalLicences(): string[] {
        const list = [];
        let licences = this.getLicences();
        if (licences) {
            licences = licences.filter((l) => l.licenceType.healthRegisterId !== AppConsts.codes.external.nareg.licence.type.dla90);
            for (let i = 0; i < licences.length; i++) {
                list.push(
                    `${this.localization.localize(licences[i].profession.sironaId, this.lSource)},
                     ${this.localization.localize(licences[i].canton.sironaId, this.lSource)},
                     ${this.resolveDate(licences[i].dateOfIssue)},
                     ${this.localization.localize(licences[i].status.sironaId, this.lSource)}`
                );
            }
        }
        return list;
    }

    hasLicenceInThisCanton(): boolean {
        let licences = this.getLicences();
        let hasLicenceInThisCanton = licences.some(x => x.canton.sironaId === abp.canton.code);
        return hasLicenceInThisCanton;
    }

    get dl90Licences(): string[] {
        const list = [];
        let licences = this.getLicences();
        if (licences) {
            licences = licences.filter((l) => l.licenceType.healthRegisterId === AppConsts.codes.external.nareg.licence.type.dla90);
            for (let i = 0; i < licences.length; i++) {
                list.push(
                    `${this.localization.localize(licences[i].profession.sironaId, this.lSource)},
                     ${this.localization.localize(licences[i].canton.sironaId, this.lSource)},
                     ${this.resolveDate(licences[i].dateOfIssue)}`
                );
            }
        }
        return list;
    }

    getPostgraduateTitlesForComparison(): HealthRegisterPostgraduateTitle[] {
        return [];
    }

    getProfessionDiplomaForComparison(professionId: string): HealthRegisterDiploma {
        return null;
    }

    getPostgraduateTitlesPrivateLawForComparison(): HealthRegisterPostgraduateTitlePrivateLaw[] {
        return [];
    }


    getPostgraduateTitlesForPerson(registerPersonId: number): Observable<string[] | string> {
        return of([]);
    }

    get hasInactiveLicences(): boolean {
        const statuses = [AppConsts.codes.professionalLicenceStatus.granted, AppConsts.codes.professionalLicenceStatus.restricted];

        const licences = this.getLicences();
        return licences?.some((l) => !statuses.includes(l.status.sironaId));
    }

    get hasRestrictedLicences(): boolean {
        const licences = this.getLicences();
        return licences?.some((l) => l.status.sironaId === AppConsts.codes.professionalLicenceStatus.restricted);
    }

    get hasInactiveLicencesInOtherCantons(): boolean {
        const statuses = [AppConsts.codes.professionalLicenceStatus.granted, AppConsts.codes.professionalLicenceStatus.restricted];

        const licences = this.getLicences();
        return licences?.some((l) => !statuses.includes(l.status.sironaId) && l.canton.sironaId !== this.cantonId);
    }

    get hasInactiveLicencesInCanton(): boolean {
        const statuses = [AppConsts.codes.professionalLicenceStatus.granted, AppConsts.codes.professionalLicenceStatus.restricted];

        const licences = this.getLicences();
        return licences?.some((l) => !statuses.includes(l.status.sironaId) && l.canton.sironaId === this.cantonId);
    }

}

